<template>
  <SoPageWithHeader title="Configurations Admin">
    <p class="text-center my-8">
      Here you can edit so.energy configurations without affecting the rest of
      the website.
    </p>
    <SoAlert
      v-if="configurationsList.error"
      title="Failed to render configurations table"
      data-testid="configurations-error-alert"
    />
    <SoDataTable
      v-else
      :headers="configurationTableHeaders"
      :items="items"
      :loading="configurationsList.isFetching"
      :sort-by="[{ key: 'name', order: 'asc' }]"
      actions-header-text="Value"
      data-testid="configurations-data-table"
    >
      <template #tableActions>
        <div v-if="hasUnSavedChanges">
          <SoButton
            title="Cancel"
            class="mr-2"
            large
            data-testid="configurations-cancel-btn"
            @click="resetChanges()"
          />
          <SoButton
            title="Review & Save"
            type="success"
            large
            data-testid="configurations-save-btn"
            @click="saveChanges()"
          />
        </div>
      </template>
      <template #itemActions="rowData">
        <SoToggle
          v-if="
            findConfigurationCopy(rowData.item.id) &&
            isBoolean(findConfigurationCopy(rowData.item.id)?.value)
          "
          :model-value="
            findConfigurationCopy(rowData.item.id)?.value === 'true'
          "
          class="toggle-align-end"
          data-testid="configurations-toggle"
          @update:model-value="(val) => toggle(val, rowData.item)"
        />
        <SoTextField
          v-if="
            findConfigurationCopy(rowData.item.id) &&
            !isBoolean(findConfigurationCopy(rowData.item.id)?.value)
          "
          v-model="
            (findConfigurationCopy(rowData.item.id) as Configuration).value
          "
          :disabled="isFieldDisabled(rowData.item)"
          class="configuration-text-field"
          data-testid="configurations-text-field"
        />
      </template>
    </SoDataTable>
  </SoPageWithHeader>
  <AdminConfigurationsModal
    v-model="showAreYouSureModal"
    :configurations="unSavedChanges"
    @success="refetch()"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import {
    SoDataTableColumnType,
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import { cloneDeep } from "lodash-es"
  import SoPageWithHeader from "@/components/templates/SoPageWithHeader.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { ConfigurationsService } from "@/services/admin/ConfigurationsService"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import { Configuration } from "@/models/admin/configuration/configuration"
  import AdminConfigurationsModal from "@/views/admin/configurations/modals/AdminConfigurationsModal.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"

  const headers: SoDataTableHeader[] = [
    {
      title: "Configuration",
      key: "name",
    },
    {
      title: "From Date",
      key: "fromDt",
    },
    {
      title: "To Date",
      key: "toDt",
    },
    {
      title: "Status",
      key: "status",
      type: SoDataTableColumnType.STATUS,
    },
  ]

  const spaygFields = [
    "SMART_PAY_AS_YOU_GO_EXECUTION_DATE_DURATION",
    "SMART_PAY_AS_YOU_GO_EXECUTION_DATE_MINIMUM_DURATION",
    "SMART_PAY_AS_YOU_GO_DAILY_LIMIT",
  ]

  export default defineComponent({
    components: {
      SoToggle,
      SoAlert,
      AdminConfigurationsModal,
      SoTextField,
      SoPageWithHeader,
      SoDataTable,
      SoButton,
    },
    data() {
      return {
        configurationTableHeaders: headers,
        configurationsList: ConfigurationsService.getConfigurationsAdminList(),
        configurationsQueryCopy: [] as Configuration[],
        unSavedChanges: [] as Configuration[],
        showAreYouSureModal: false,
      }
    },
    computed: {
      Configuration() {
        return Configuration
      },
      items(): SoDataTableItem[] {
        return (this.configurationsList.data || []).map(
          (item: Configuration) => ({
            ...item,
            fromDt: item.fromDt.format("DD MMM YYYY"),
            toDt: item.toDt ? item.toDt.format("DD MMM YYYY") : "",
            status: {
              value: item.status.text,
              props: { status: item.status },
            },
          }),
        ) as SoDataTableItem[]
      },
      hasUnSavedChanges() {
        return (
          JSON.stringify(this.configurationsList.data) !==
          JSON.stringify(this.configurationsQueryCopy)
        )
      },
    },
    watch: {
      "configurationsList.data"(value) {
        this.configurationsQueryCopy = cloneDeep(value)
      },
    },
    mounted() {
      this.configurationsQueryCopy = cloneDeep(this.configurationsList.data)
    },
    methods: {
      findConfigurationCopy(id: number): Configuration | undefined {
        return this.configurationsQueryCopy.find(
          (configuration: Configuration) => configuration.id === id,
        )
      },
      saveChanges() {
        this.unSavedChanges = this.configurationsQueryCopy.filter(
          (configurationCopy) =>
            configurationCopy.value !==
            this.configurationsList.data.find(
              ({ id }) => id === configurationCopy.id,
            )?.value,
        )
        this.showAreYouSureModal = true
      },
      refetch() {
        this.configurationsList.refetch()
      },
      resetChanges() {
        this.configurationsQueryCopy = cloneDeep(this.configurationsList.data)
      },
      isBoolean(value: string | null | undefined) {
        return value === "true" || value === "false"
      },
      isDailyLimiterEnabled() {
        const limiterConfig = this.configurationsQueryCopy.find(
          ({ name }) =>
            name?.toUpperCase() === "SMART_PAY_AS_YOU_GO_DAILY_LIMIT_ENABLER",
        )
        return limiterConfig?.value === "true"
      },
      isSpaygField(configuration: Configuration) {
        if (!this.isDailyLimiterEnabled()) return false
        return spaygFields.includes(configuration.name.toUpperCase())
      },
      isFieldDisabled(item: Configuration) {
        return !item.editable || !this.isSpaygField(item)
      },
      toggle(val: boolean, configuration: Configuration) {
        const configurationCopy = this.findConfigurationCopy(configuration.id)
        if (configurationCopy) {
          configurationCopy.value = val ? "true" : "false"
        }
      },
    },
  })
</script>

<style scoped>
  .toggle-align-end {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    width: 100%;
  }

  .configuration-text-field {
    position: relative;
    top: 4%;
    width: 220px;
    margin-left: auto;
  }
</style>
